import React, { useEffect, useMemo, useState } from "react";
import api, { fileUrl, asArray, BACKEND_URL } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { Globe2 } from "lucide-react";

const META = {
    media: {
        title_ar: "الوصول العالمي",
        title_en: "Global Reach",
        overline_ar: "شبكتنا الإعلامية حول العالم",
        overline_en: "Our global media network",
        Icon: Globe2,
        categories: [
            { key: "europe",       ar: "أوروبا",        en: "Europe" },
            { key: "usa",          ar: "الولايات المتحدة", en: "USA" },
            { key: "asia",         ar: "آسيا",          en: "Asia" },
            { key: "middle_east",  ar: "الشرق الأوسط",  en: "Middle East" },
        ],
        clickEffect: "bounce",
    },
};

function ensureLink(rel, href, attrs = {}) {
    if (!href || typeof document === "undefined") return;
    const selector = `link[rel="${rel}"][href="${href}"]`;
    if (document.querySelector(selector)) return;

    const link = document.createElement("link");
    link.rel = rel;
    link.href = href;
    Object.entries(attrs).forEach(([key, value]) => {
        if (value != null) link.setAttribute(key, value);
    });
    document.head.appendChild(link);
}

function BrandTile({ item, mode, lang, priority = false }) {
    const [bounce, setBounce] = useState(false);
    const [failed, setFailed] = useState(false);
    const name = (lang === "ar" ? item.name_ar : item.name_en) || item.name_ar || item.name_en;
    const src = item.image && !failed ? fileUrl(item.image) : "";

    const onClick = () => {
        if (mode === "bounce") {
            setBounce(true);
            setTimeout(() => setBounce(false), 700);
        }
    };

    return (
        <button
            type="button"
            onClick={onClick}
            className={`group relative bg-white border border-black/5 aspect-[4/3] overflow-hidden flex items-center justify-center p-5 transition-all duration-300 hover:shadow-xl hover:border-[#0060A7]/30 ${bounce ? "animate-bounce-up" : ""}`}
            data-testid={`brand-tile-${item.id}`}
            aria-label={name}
        >
            {src ? (
                <img src={src} alt={name} width="320" height="180" className="max-w-[86%] max-h-[68%] object-contain transition-transform duration-300 group-hover:scale-105" loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : "auto"} decoding="async" onError={() => setFailed(true)} />
            ) : (
                <div className="text-center text-[#002951] heading text-sm font-bold leading-snug">
                    {name}
                </div>
            )}
        </button>
    );
}

export default function BrandSectionPage({ section }) {
    const { lang, isRTL } = useI18n();
    const { settings } = useSiteSettings();
    const meta = META[section];
    const [items, setItems] = useState([]);
    const [activeCat, setActiveCat] = useState(meta.categories?.[0]?.key || "");

    useEffect(() => {
        api.get(`/brand-items?section=${section}`).then((r) => setItems(asArray(r.data, "items"))).catch(() => setItems([]));
    }, [section]);

    const filtered = useMemo(() => {
        if (meta.categories.length === 0) return items;
        return items.filter((x) => x.category === activeCat);
    }, [items, activeCat, meta.categories.length]);

    useEffect(() => {
        if (section !== "media" || filtered.length === 0) return;

        ensureLink("preconnect", BACKEND_URL, { crossOrigin: "" });
        ensureLink("dns-prefetch", BACKEND_URL);

        filtered.slice(0, 12).forEach((item, index) => {
            if (!item.image) return;
            const href = fileUrl(item.image);
            try {
                const origin = new URL(href).origin;
                ensureLink("preconnect", origin, { crossOrigin: "" });
                ensureLink("dns-prefetch", origin);
            } catch (_) {
                // Ignore malformed third-party URLs; the image tag fallback will handle them.
            }
            if (index < 8) {
                ensureLink("preload", href, { as: "image", fetchpriority: "high" });
            }
        });
    }, [section, filtered]);

    const settingsKey = section === "media" ? "global" : section;
    const title = settings[`section_${settingsKey}_title_${lang}`] || (lang === "ar" ? meta.title_ar : meta.title_en);
    const overline = settings[`section_${settingsKey}_overline_${lang}`] || (lang === "ar" ? meta.overline_ar : meta.overline_en);
    const Icon = meta.Icon;

    return (
        <div data-testid={`${section}-page`}>
            <section className="bg-[#EFF0F1] py-20 md:py-28">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
                    <div className="section-eyebrow mb-4 text-[#0060A7] plus-mark">
                        {overline}
                    </div>
                    <div className="flex items-center gap-4">
                        <Icon size={36} strokeWidth={1.4} className="text-[#002951]" />
                        <h1 className="heading page-hero-oneline text-[#002951]">
                            {title}
                        </h1>
                    </div>

                    {meta.categories.length > 0 && (
                        <div className="mt-10 flex flex-wrap gap-2" data-testid="brand-filters">
                            {meta.categories.map((c) => (
                                <button
                                    key={c.key}
                                    onClick={() => setActiveCat(c.key)}
                                    className={`px-5 py-2 text-sm uppercase tracking-[0.2em] border transition-colors ${
                                        activeCat === c.key
                                            ? "bg-[#002951] text-white border-[#002951]"
                                            : "border-black/15 hover:border-[#0060A7] hover:text-[#0060A7]"
                                    }`}
                                    data-testid={`brand-cat-${c.key}`}
                                >
                                    {lang === "ar" ? c.ar : c.en}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <section className="bg-white py-16 md:py-20">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
                    {filtered.length === 0 ? (
                        <div className="text-center py-20 text-[#6B7280]">{isRTL ? "لا توجد عناصر في هذا القسم بعد" : "Nothing here yet"}</div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                            {filtered.map((it, i) => (
                                <BrandTile key={it.id} item={it} mode={meta.clickEffect} lang={lang} priority={section === "media" && i < 12} />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
